import { t } from "i18next";
import { parsePhoneNumberFromString, AsYouType, isValidPhoneNumber } from "libphonenumber-js";

// Mapping entre les indicatifs et les codes pays ISO
const PHONE_COUNTRY_MAPPING = {
  "+355": "AL", // Albanie
  "+376": "AD", // Andorre
  "+43": "AT", // Autriche
  "+61": "AU", // Australie
  "+32": "BE", // Belgique
  "+880": "BD", // Bangladesh
  "+359": "BG", // Bulgarie
  "+387": "BA", // Bosnie-Herzégovine
  "+55": "BR", // Brésil
  "+375": "BY", // Biélorussie
  "+1": "US", // États-Unis/Canada
  "+86": "CN", // Chine
  "+41": "CH", // Suisse
  "+357": "CY", // Chypre
  "+420": "CZ", // République tchèque
  "+49": "DE", // Allemagne
  "+45": "DK", // Danemark
  "+213": "DZ", // Algérie
  "+20": "EG", // Égypte
  "+372": "EE", // Estonie
  "+34": "ES", // Espagne
  "+358": "FI", // Finlande
  "+33": "FR", // France
  "+44": "UK", // Royaume-Uni
  "+30": "GR", // Grèce
  "+385": "HR", // Croatie
  "+36": "HU", // Hongrie
  "+62": "ID", // Indonésie
  "+353": "IE", // Irlande
  "+91": "IN", // Inde
  "+354": "IS", // Islande
  "+39": "IT", // Italie
  "+81": "JP", // Japon
  "+82": "KR", // Corée du Sud
  "+383": "XK", // Kosovo
  "+371": "LV", // Lettonie
  "+423": "LI", // Liechtenstein
  "+94": "LK", // Sri Lanka
  "+370": "LT", // Lituanie
  "+352": "LU", // Luxembourg
  "+212": "MA", // Maroc
  "+377": "MC", // Monaco
  "+373": "MD", // Moldavie
  "+382": "ME", // Monténégro
  "+389": "MK", // Macédoine du Nord
  "+356": "MT", // Malte
  "+60": "MY", // Malaisie
  "+31": "NL", // Pays-Bas
  "+234": "NG", // Nigéria
  "+47": "NO", // Norvège
  "+64": "NZ", // Nouvelle-Zélande
  "+92": "PK", // Pakistan
  "+48": "PL", // Pologne
  "+351": "PT", // Portugal
  "+63": "PH", // Philippines
  "+40": "RO", // Roumanie
  "+381": "RS", // Serbie
  "+7": "RU", // Russie
  "+46": "SE", // Suède
  "+65": "SG", // Singapour
  "+386": "SI", // Slovénie
  "+421": "SK", // Slovaquie
  "+378": "SM", // Saint-Marin
  "+66": "TH", // Thaïlande
  "+216": "TN", // Tunisie
  "+90": "TR", // Turquie
  "+380": "UA", // Ukraine
  "+998": "UZ", // Ouzbékistan
  "+379": "VA", // Vatican
  "+84": "VN", // Vietnam
  "+971": "AE", // Émirats arabes unis
  "+27": "ZA", // Afrique du Sud
};

/**
 * Formate un numéro de téléphone en temps réel pendant la saisie
 * @param {string} phoneNumber - Le numéro de téléphone à formater
 * @param {string} countryCode - Le code pays (ex: "FR", "US", etc.)
 * @returns {string} - Le numéro formaté
 */
export function formatPhoneNumberAsYouType(phoneNumber, countryCode) {
  try {
    if (!phoneNumber) return "";

    // Nettoyer le numéro (garder seulement les chiffres, espaces, tirets, parenthèses)
    const cleanNumber = phoneNumber.replace(/[^\d\s\-\(\)\+]/g, "");

    const asYouType = new AsYouType(countryCode);
    return asYouType.input(cleanNumber);
  } catch (error) {
    console.warn("Erreur lors du formatage du numéro:", error);
    return phoneNumber;
  }
}

export function parseExistingPhone(fullPhoneNumber) {
  if (!fullPhoneNumber) {
    return { indicatif: "+33", localNumber: "" };
  }

  try {
    const parsed = parsePhoneNumberFromString(fullPhoneNumber);
    if (parsed) {
      const indicatif = `+${parsed.countryCallingCode}`;
      const localNumber = parsed.nationalNumber;
      return { indicatif, localNumber };
    }
  } catch (error) {
    console.warn("Erreur lors du parsing du numéro existant:", error);
  }

  // Fallback: essayer de détecter l'indicatif manuellement
  if (fullPhoneNumber.startsWith("+")) {
    for (const phoneIndicatif of phoneIndicatifs) {
      if (fullPhoneNumber.startsWith(phoneIndicatif.id)) {
        return {
          indicatif: phoneIndicatif.id,
          localNumber: fullPhoneNumber.slice(phoneIndicatif.id.length),
        };
      }
    }
  }

  return { indicatif: "+33", localNumber: fullPhoneNumber };
}

/**
 * Valide un numéro de téléphone complet (avec indicatif)
 * @param {string} phoneNumber - Le numéro de téléphone complet
 * @param {string} countryCode - Le code pays optionnel
 * @returns {object} - { isValid: boolean, formattedNumber: string, internationalNumber: string }
 */
export function validateAndFormatPhoneNumber(phoneNumber, countryCode = null) {
  try {
    if (!phoneNumber) {
      return { isValid: false, formattedNumber: "", internationalNumber: "", error: t("phone.validation.missing_number") };
    }

    // Essayer de parser le numéro
    const parsed = parsePhoneNumberFromString(phoneNumber, countryCode);

    if (!parsed) {
      return { isValid: false, formattedNumber: phoneNumber, internationalNumber: "", error: t("phone.validation.invalid_format") };
    }

    const isValid = parsed.isValid();

    return {
      isValid,
      formattedNumber: isValid ? parsed.formatNational() : phoneNumber,
      internationalNumber: isValid ? parsed.formatInternational() : "",
      e164Format: isValid ? parsed.format("E.164") : "",
      error: isValid ? null : t("phone.validation.invalid_for_country"),
    };
  } catch (error) {
    console.warn("Erreur lors de la validation du numéro:", error);
    return {
      isValid: false,
      formattedNumber: phoneNumber,
      internationalNumber: "",
      error: t("phone.validation.validation_error"),
    };
  }
}

/**
 * Obtient le code pays ISO à partir de l'indicatif téléphonique
 * @param {string} phoneIndicatif - L'indicatif téléphonique (ex: "+33")
 * @returns {string} - Le code pays ISO (ex: "FR")
 */
export function getCountryCodeFromIndicatif(phoneIndicatif) {
  return PHONE_COUNTRY_MAPPING[phoneIndicatif] || null;
}

/**
 * Vérifie si un numéro de téléphone est valide pour un pays donné
 * @param {string} phoneNumber - Le numéro sans l'indicatif
 * @param {string} indicatif - L'indicatif téléphonique
 * @returns {boolean}
 */
export function isPhoneNumberValidForCountry(phoneNumber, indicatif) {
  try {
    const countryCode = getCountryCodeFromIndicatif(indicatif);
    if (!countryCode) return false;

    const fullNumber = indicatif + phoneNumber;
    return isValidPhoneNumber(fullNumber, countryCode);
  } catch (error) {
    console.warn("Erreur lors de la validation:", error);
    return false;
  }
}

/**
 * Nettoie un numéro de téléphone en gardant seulement les chiffres utiles
 * @param {string} phoneNumber - Le numéro à nettoyer
 * @returns {string}
 */
export function cleanPhoneNumber(phoneNumber) {
  if (!phoneNumber) return "";
  // Garder seulement les chiffres, mais préserver les espaces pour le formatage
  return phoneNumber.replace(/[^\d\s]/g, "");
}

export function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}
export function truncate(str, n) {
  return str.length > n ? str.slice(0, n - 1) + "..." : str;
}
export function currencyToStr(currency) {
  if (!currency) return "";
  switch (currency) {
    case "EUR":
      return "€";
    case "USD":
      return "$";
    case "GBP":
      return "£";
    default:
      return currency;
  }
}
export function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
}

export function getClientName(client) {
  if (!client) return "";
  if (client.entityType === "COMPANY") return client.companyName;
  return `${client.firstname} ${client.lastname}`;
}

export function getTaxRate(taxType) {
  if (taxType == "FR 20%") return 0.2;
  if (taxType == "NL 21%") return 0.21;
  if (taxType == "IE 23%") return 0.23;
  return 0;
}
export const copyToClipboard = (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text);
  } else {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "absolute";
    textArea.style.opacity = 0;
    document.body.appendChild(textArea);
    textArea.select();
    return new Promise((res, rej) => {
      // here the magic happens
      document.execCommand("copy") ? res() : rej();
      textArea.remove();
    });
  }
};

export function readFileAsync(file) {
  return new Promise((resolve, reject) => {
    let reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function getTransactionAccountName(transaction) {
  if (!transaction) return "";
  if (transaction.accountDetails === "petty_cash") return t("petty_cash");
  if (transaction.accountDetails === "savings") return t("savings");
  const name = `${transaction.accountDetails ?? ""} ${transaction.accountOwner ?? ""}`.trim();
  if (name === "" || name === "NOT PROVIDED") return transaction.bankName;
  return name;
}
export function getAccountName(account) {
  if (!account) return "";
  if (account.details === "petty_cash") return t("petty_cash");
  if (account.details === "savings") return t("savings");
  const name = `${account.details ?? ""} ${account.ownerName ?? ""}`.trim();
  if (name === "" || name === "NOT PROVIDED") return account?.Requisition?.NordigenInstitution?.name ?? "";
  return name;
}

export function hexToRgb(hex, opacity = 1) {
  if (!hex) return null;
  const bigint = parseInt(hex.replace("#", ""), 16);
  const res = `rgba(${(bigint >> 16) & 255}, ${(bigint >> 8) & 255}, ${bigint & 255}, ${opacity})`;
  return res;
}
